/**
 * テンプレートCSVダウンロード用のヘッダー導出（#4728）。
 *
 * - 通常フォーマット: SHIPMENT_ORDER_CSV_STANDARD_HELP の列仕様（基本26列+追加情報8列+運行計画8列+任意の列1列）
 *   をそのままヘッダー行として書き出す。これは standard-format.ts が参照する実パーサーの
 *   受け付け列名と同じ正典（SHIPMENT_ORDER_CSV_STANDARD_HELP）から導出するため齟齬が生まれない。
 * - 保存したフォーマット: 保存時の列の並びどおりに、保存時のヘッダー名を書き出す
 *   （ヘッダー名が無い列は、その列に紐づけた項目名で埋める）。
 * - フォーマット外（adhoc）はマッピングを事前に持たないため対象外
 *   （「テンプレート」という概念がまだ成立しない。データが先、マッピングは後）。
 */

import { SHIPMENT_ORDER_CSV_STANDARD_HELP } from '@/constants/common/message.constants'

import { LOGI_FIELDS } from './logi-fields'
import { type SavedFormat } from './saved-formats'

/** 標準フォーマットのテンプレートCSVファイル名（英数字のみ、i18nハードコード規約の対象外）。 */
export const STANDARD_CSV_TEMPLATE_FILENAME =
  'shipment_order_standard_template.csv'

/**
 * 通常フォーマットのテンプレートヘッダー（基本26列+追加情報8列+運行計画8列+任意の列「取消」「まとめキー」=44列）。
 * 列の並び順は SHIPMENT_ORDER_CSV_STANDARD_HELP の表示順（ヘルプDrawerと同じ）に揃える。
 * 任意の列も入れるのは、列仕様に載っている列はテンプレートからも書き始められるようにするため（使わなければ空欄のままでよい）。
 */
export const buildStandardTemplateHeaders = (): string[] => {
  const {
    BASE_COLUMNS,
    ADD_INFO_COLUMNS,
    DISPATCHED_COLUMNS,
    OPTIONAL_COLUMNS
  } = SHIPMENT_ORDER_CSV_STANDARD_HELP
  return [
    ...BASE_COLUMNS,
    ...ADD_INFO_COLUMNS,
    ...DISPATCHED_COLUMNS,
    ...OPTIONAL_COLUMNS
  ].map((column) => column.name)
}

/**
 * 保存したフォーマットのテンプレートヘッダー（保存時に使ったCSVの列名をそのまま再現する）。
 */
export const buildSavedFormatTemplateHeaders = (
  format: SavedFormat
): string[] => {
  const labelOf = new Map(LOGI_FIELDS.map((f) => [f.value, f.label]))
  return [...format.setting.columns]
    .sort((a, b) => a.columnIndex - b.columnIndex)
    .map((c) => c.header ?? labelOf.get(c.field) ?? c.field)
}

/**
 * フォーマット名をファイル名の一部として使うための最小サニタイズ
 * （フォーマット名はユーザーが自由に入力するため、パス区切り等を除去する）。
 */
export const sanitizeFileNameSegment = (raw: string): string => {
  const cleaned = raw.replace(/[\\/:*?"<>|]/g, '_').trim()
  return cleaned === '' ? 'format' : cleaned
}
